import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";

import { env } from "@musicfy/env.mjs";

import { type AvailablePlans } from "./Subscriptions";

/**
 * Stripe consumer subscription catalog (Packages plans).
 *
 * Test and Live catalogs are env-split (same pattern as Paddle PriceId).
 * Amounts match Paddle PRICE_ID_TO_PRICE_MAP in Subscriptions.ts.
 */

const isProduction = env.NEXT_PUBLIC_VERCEL_ENV === "production";

/** Test-mode product IDs (musicfy_consumer_v1). */
const StripeProductIdTest = {
  starter: "prod_UxozalF2UwFrSQ",
  pro: "prod_UxozrvWF1IILIN",
  studio: "prod_UxozOEpbXHAYsq",
  label: "prod_UxozDXeBi6FhA4",
} as const;

/** Live-mode product IDs (musicfy_consumer_v1). */
const StripeProductIdLive = {
  starter: "prod_V4b848FDkv7pSV",
  pro: "prod_V4b8cvk2TczhTF",
  studio: "prod_V4b8FjveSCVnJh",
  label: "prod_V4b8Kio81iZE97",
} as const;

/** Test-mode price IDs (musicfy_consumer_v1). */
const StripePriceIdTest = {
  starter_month: "price_1TxtLXKrcWFDJGPKAvGHAHy9",
  starter_year: "price_1TxtLXKrcWFDJGPKcUwbQHZk",
  pro_month: "price_1TxtLYKrcWFDJGPK2qMrykfn",
  pro_year: "price_1TxtLYKrcWFDJGPKKkwBqeTq",
  studio_month: "price_1TxtLZKrcWFDJGPKMQazERoI",
  studio_year: "price_1TxtLZKrcWFDJGPK8HRgOES5",
  label_month: "price_1TxtLaKrcWFDJGPKZ4Abe3nv",
  label_year: "price_1TxtLaKrcWFDJGPKUvojQh1Z",
} as const;

/** Live-mode price IDs (musicfy_consumer_v1). */
const StripePriceIdLive = {
  starter_month: "price_1U4RvwKrcWFDJGPKmYxYCKQT",
  starter_year: "price_1U4RvwKrcWFDJGPKzvBDtY1f",
  pro_month: "price_1U4RvxKrcWFDJGPKEkTnJuM3",
  pro_year: "price_1U4RvxKrcWFDJGPKFNpa1jjY",
  studio_month: "price_1U4RvyKrcWFDJGPKrykc5bpC",
  studio_year: "price_1U4RvyKrcWFDJGPKc83J7P29",
  label_month: "price_1U4RvyKrcWFDJGPKdRqzjcxO",
  label_year: "price_1U4RvzKrcWFDJGPK6qzk1O8I",
} as const;

export const StripeProductId = isProduction
  ? StripeProductIdLive
  : StripeProductIdTest;

export const StripePriceId = isProduction
  ? StripePriceIdLive
  : StripePriceIdTest;

export type TStripePriceId = (typeof StripePriceId)[keyof typeof StripePriceId];
export type TStripeProductId =
  (typeof StripeProductId)[keyof typeof StripeProductId];

/**
 * Cancel-retention coupons (Paddle parity). Same custom IDs in Test and Live.
 * Monthly: 100% off next renewal once. Yearly: 50% off next renewal once.
 */
export const StripeRetentionCouponId = {
  month: "musicfy_retention_monthly_100",
  year: "musicfy_retention_yearly_50",
} as const;

export type TStripeRetentionCouponId =
  (typeof StripeRetentionCouponId)[keyof typeof StripeRetentionCouponId];

/** Pick retention coupon by billing interval (matches Paddle retain offers). */
export const getStripeRetentionCouponIdForInterval = (
  interval: SubscriptionIntervals
): TStripeRetentionCouponId => {
  return interval === SubscriptionIntervals.year
    ? StripeRetentionCouponId.year
    : StripeRetentionCouponId.month;
};

export const STRIPE_PRICE_ID_TO_PLAN_MAP = {
  [StripePriceId.starter_month]: SubscriptionPlans.starter,
  [StripePriceId.starter_year]: SubscriptionPlans.starter,
  [StripePriceId.pro_month]: SubscriptionPlans.pro,
  [StripePriceId.pro_year]: SubscriptionPlans.pro,
  [StripePriceId.studio_month]: SubscriptionPlans.studio,
  [StripePriceId.studio_year]: SubscriptionPlans.studio,
  [StripePriceId.label_month]: SubscriptionPlans.label,
  [StripePriceId.label_year]: SubscriptionPlans.label,
} as { [x in TStripePriceId]: AvailablePlans };

export const STRIPE_PRICE_ID_TO_INTERVAL_MAP = {
  [StripePriceId.starter_month]: SubscriptionIntervals.month,
  [StripePriceId.starter_year]: SubscriptionIntervals.year,
  [StripePriceId.pro_month]: SubscriptionIntervals.month,
  [StripePriceId.pro_year]: SubscriptionIntervals.year,
  [StripePriceId.studio_month]: SubscriptionIntervals.month,
  [StripePriceId.studio_year]: SubscriptionIntervals.year,
  [StripePriceId.label_month]: SubscriptionIntervals.month,
  [StripePriceId.label_year]: SubscriptionIntervals.year,
} as { [x in TStripePriceId]: SubscriptionIntervals };

export const STRIPE_PRICE_ID_TO_PRICE_MAP = {
  [StripePriceId.starter_month]: 999,
  [StripePriceId.starter_year]: 9599,
  [StripePriceId.pro_month]: 2499,
  [StripePriceId.pro_year]: 23999,
  [StripePriceId.studio_month]: 6999,
  [StripePriceId.studio_year]: 67199,
  [StripePriceId.label_month]: 14999,
  [StripePriceId.label_year]: 133999,
} as { [x in TStripePriceId]: number };

export const isStripeConsumerPriceId = (
  priceId: string
): priceId is TStripePriceId => {
  return Object.prototype.hasOwnProperty.call(
    STRIPE_PRICE_ID_TO_PLAN_MAP,
    priceId
  );
};

const STRIPE_PRICE_BY_PLAN_AND_INTERVAL: Record<
  AvailablePlans,
  Record<SubscriptionIntervals, TStripePriceId>
> = {
  [SubscriptionPlans.starter]: {
    [SubscriptionIntervals.month]: StripePriceId.starter_month,
    [SubscriptionIntervals.year]: StripePriceId.starter_year,
  },
  [SubscriptionPlans.pro]: {
    [SubscriptionIntervals.month]: StripePriceId.pro_month,
    [SubscriptionIntervals.year]: StripePriceId.pro_year,
  },
  [SubscriptionPlans.studio]: {
    [SubscriptionIntervals.month]: StripePriceId.studio_month,
    [SubscriptionIntervals.year]: StripePriceId.studio_year,
  },
  [SubscriptionPlans.label]: {
    [SubscriptionIntervals.month]: StripePriceId.label_month,
    [SubscriptionIntervals.year]: StripePriceId.label_year,
  },
};

export const getStripePriceIdForPlan = (
  plan: AvailablePlans,
  interval: SubscriptionIntervals
): TStripePriceId => {
  return STRIPE_PRICE_BY_PLAN_AND_INTERVAL[plan][interval];
};
